import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ScanLine, Loader2, CheckCircle2, PackageSearch, Camera, X } from "lucide-react";
import { api, apiError, fileUrl } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, money } from "../components/common";
import { QrScanner } from "../components/QrScanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function ClaimReceipt() {
  const { code } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [value, setValue] = useState(code || "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const [scanning, setScanning] = useState(false);

  const lookup = async (c) => {
    if (!c) return;
    setLooking(true); setPreview(null);
    try {
      const { data } = await api.get(`/receipts/lookup/${c}`);
      setPreview(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setLooking(false); }
  };

  useEffect(() => { if (code) lookup(code); }, [code]);

  const onScanned = (c) => {
    setScanning(false);
    setValue(c);
    lookup(c);
    toast.success(`Scanned code ${c}`);
  };

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/receipts/claim", { claim_code: value.trim().toUpperCase() });
      qc.invalidateQueries({ queryKey: ["mine"] });
      qc.invalidateQueries({ queryKey: ["warranties"] });
      toast.success("Receipt added to your wallet!");
      navigate(`/wallet/receipt/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <AppLayout>
      <PageHeader eyebrow="Claim" title="Claim a receipt" subtitle="Scanned a QR from a retailer? Enter the claim code to add it to your wallet." />

      <div className="mx-auto max-w-lg">
        <div className="rounded-2xl border border-border bg-card p-7">
          <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-brand-muted text-brand">
            <ScanLine className="h-7 w-7" />
          </div>

          {scanning ? (
            <div className="mb-5" data-testid="scanner-wrap">
              <QrScanner onScan={onScanned} onError={() => { setScanning(false); toast.error("Couldn't access camera. Enter the code manually."); }} />
              <Button variant="outline" className="mt-3 w-full" onClick={() => setScanning(false)} data-testid="stop-scan-btn">
                <X className="mr-2 h-4 w-4" />Stop camera
              </Button>
            </div>
          ) : (
            <Button variant="outline" className="mb-4 w-full" onClick={() => setScanning(true)} data-testid="scan-qr-btn">
              <Camera className="mr-2 h-4 w-4" />Scan QR with camera
            </Button>
          )}

          <div className="flex gap-2">
            <Input value={value} onChange={(e) => setValue(e.target.value.toUpperCase())} placeholder="Enter claim code (e.g. DEMO1234)"
              className="font-mono tracking-widest" data-testid="claim-code-input" />
            <Button variant="outline" onClick={() => lookup(value.trim().toUpperCase())} disabled={looking || !value} data-testid="lookup-btn">
              {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageSearch className="h-4 w-4" />}
            </Button>
          </div>

          {preview && (
            <div className="mt-5 animate-fade-up rounded-xl border border-border bg-secondary p-5" data-testid="claim-preview">
              <div className="flex items-center gap-3">
                {preview.store_logo_path && <img src={fileUrl(preview.store_logo_path)} alt="" className="h-9 w-9 rounded-lg object-cover" />}
                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{preview.store_name}</p>
              </div>
              <div className="mt-2 space-y-1">
                {preview.items?.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm"><span>{i.qty}× {i.name}</span><span className="font-mono">{money(i.price * i.qty)}</span></div>
                ))}
              </div>
              <div className="mt-2 flex justify-between border-t border-border pt-2 font-bold"><span>Total</span><span className="font-mono">{money(preview.total)}</span></div>
              {preview.already_claimed ? (
                <p className="mt-4 flex items-center gap-2 text-sm text-warning"><CheckCircle2 className="h-4 w-4" />This receipt has already been claimed.</p>
              ) : (
                <Button onClick={claim} disabled={busy} className="mt-4 w-full bg-brand text-white hover:bg-brand-hover" data-testid="confirm-claim-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to my wallet"}
                </Button>
              )}
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">Tip: scanning a ProofPass QR on your phone opens this page with the code pre-filled.</p>
      </div>
    </AppLayout>
  );
}
